import { KnownSortOrder } from '@groundfloor-platform/mgmt-service-g-sdk';
import { InfiniteScrollProps } from '../../../../ui/components/pure/list-table-wrappers/infinite-table.component';
import { useQueryGetProject } from '../../../../core/queries/projects.queries';
import useSearchParams, { UseSearchParamsOptions } from '../../../../hooks/use-search-params.hook';
import { useInfiniteQueryCarriersList } from '../../carriers.queries';
import {
  CARRIER_REVIEW_ALL,
  CARRIER_REVIEW_FLAGGED,
  CARRIER_STATUS_ACTIVE,
  CarriersListControlsState,
  includeFlaggedFromReviewFilter
} from '../logic/carriers-list-query.map';

// The kiosk's own order, which is also the endpoint's default. Sent explicitly so the column
// header shows the sort and the URL carries it for sharing.
const initialControlsState: CarriersListControlsState = {
  sortBy: 'searchPriority',
  sortOrder: KnownSortOrder.Asc,
  searchTerm: '',
  status: CARRIER_STATUS_ACTIVE,
  review: CARRIER_REVIEW_ALL
};

// The same list opened from "Pending Carriers": only those awaiting approval, still widenable.
const pendingControlsState: CarriersListControlsState = {
  ...initialControlsState,
  review: CARRIER_REVIEW_FLAGGED
};

const searchParamOptions: UseSearchParamsOptions<CarriersListControlsState> = {
  parse: {
    excludedValues: [''],
    convertBooleans: true
  },
  stringify: {
    excludedValues: [null, ''],
    stringifyDates: true
  },
  config: {
    debouncedValues: ['searchTerm']
  }
};

export function useCarriersList(projectID: string, pending = false) {
  const project = useQueryGetProject(projectID);
  const [formData, queryData, setFormData] = useSearchParams(
    pending ? pendingControlsState : initialControlsState,
    searchParamOptions
  );

  const result = useInfiniteQueryCarriersList(projectID, {
    ...queryData,
    includeDeleted: queryData.status !== CARRIER_STATUS_ACTIVE,
    includeFlagged: includeFlaggedFromReviewFilter(queryData.review)
  });

  const infiniteScroll: InfiniteScrollProps = {
    hasNextPage: result.hasNextPage || false,
    isFetchingNextPage: result.isFetchingNextPage,
    fetchNextPage: result.fetchNextPage,
    onScrollToBottom() {
      result.fetchNextPage();
    }
  };

  return {
    project,
    result,
    infiniteScroll,
    formData,
    setFormData
  } as const;
}
